import type { AABB, Vec3 } from '../pc/colliders'
import { MAX_STEP, moveAndResolveCollisions } from '../pc/colliders'

// Keep the acceptance math independent of the heavy interior registry chain.
// This matches interiors.ts and floorPrograms.ts; callers may override it.
const INTERIOR_ISLAND_Z = -1000

export type HqAcceptanceFloor = 0 | 1 | 2 | 3
export const HQ_ACCEPTANCE_VIEW_IDS = [
  'l0-core-up',
  'l0-bay',
  'l1-bay',
  'l1-west-rooms',
  'l2-boardroom-bay',
  'l3-core-down',
  'l3-bay',
] as const
export const HQ_FACADE_ACCEPTANCE_VIEW_IDS = [
  'l0-facade-hero',
  'l1-facade-hero',
  'l2-facade-hero',
  'l3-facade-hero',
] as const
/** Detail views used for asset/art-direction QA without expanding the core tour. */
export const HQ_SYNTY_ACCEPTANCE_VIEW_IDS = [
  'l1-focus-studio',
  'l1-team-huddle',
  'l1-synty-characters',
  'l2-synty-characters',
  'l0-lounge-detail',
  'l2-boardroom-detail',
  'l2-kitchen-detail',
  'l2-lounge-detail',
  'l3-penthouse-detail',
  'l3-executive-detail',
] as const
export type HqAcceptanceViewId =
  | typeof HQ_ACCEPTANCE_VIEW_IDS[number]
  | typeof HQ_FACADE_ACCEPTANCE_VIEW_IDS[number]
  | typeof HQ_SYNTY_ACCEPTANCE_VIEW_IDS[number]

export interface HqAcceptanceViewPose {
  id: HqAcceptanceViewId
  floor: HqAcceptanceFloor
  x: number
  y: number
  /** Walk surface supporting the fixed acceptance pose. */
  supportY: number
  z: number
  yawDeg: number
  pitchDeg: number
  purpose: string
}

export interface HqTraversalCheckpoint {
  leg: 'up' | 'down'
  floor: HqAcceptanceFloor
  x: number
  y: number
  z: number
  grounded: boolean
}

export interface HqTraversalArtifact {
  schema: 'arena.hq-traversal.v1'
  status: 'measured'
  capturedAt: string
  colliderCount: number
  frames: number
  steppedUp: number
  steppedDown: number
  minY: number
  maxY: number
  checkpoints: HqTraversalCheckpoint[]
  failures: string[]
  passed: boolean
}

export interface HqProgramAcceptanceZone {
  id: string
  floor: HqAcceptanceFloor
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number }
  access: readonly [number, number]
  routeNode: string
}

export interface HqProgramAcceptanceFloor {
  floor: HqAcceptanceFloor
  nodes: ReadonlyArray<{ id: string; x: number; z: number }>
}

export interface HqProgramTraversalCheckpoint {
  zoneId: string
  floor: HqAcceptanceFloor
  x: number
  y: number
  z: number
  grounded: boolean
  inBounds: boolean
  noFall: boolean
  doorwayReached: boolean
}

export interface HqProgramTraversalArtifact {
  schema: 'arena.hq-program-traversal.v1'
  status: 'measured'
  capturedAt: string
  colliderCount: number
  frames: number
  checkpoints: HqProgramTraversalCheckpoint[]
  failures: string[]
  gate: {
    expectedZoneCount: 24
    zoneCountPass: boolean
    groundedPass: boolean
    inBoundsPass: boolean
    noFallPass: boolean
    doorwayPass: boolean
    passed: boolean
  }
  passed: boolean
}

const FLOOR_Y = [0, 4.5, 9, 13.5] as const
const PLAYER_RADIUS = 0.3
const PLAYER_HEIGHT = 1.8
const WALK_STEP = 0.06
const TARGET_EPSILON = 0.035
const MAX_SEGMENT_FRAMES = 420

/**
 * Representative real-renderer views for the P2–P6 visual gates. The low and
 * high views deliberately frame the Core in opposite directions; the middle
 * floors look east through their program toward the recurring bay facade.
 */
export function hqAcceptanceView(
  floor: HqAcceptanceFloor,
  islandZ = INTERIOR_ISLAND_Z,
): HqAcceptanceViewPose {
  const ids: Record<HqAcceptanceFloor, HqAcceptanceViewId> = {
    0: 'l0-core-up',
    1: 'l1-bay',
    2: 'l2-boardroom-bay',
    3: 'l3-core-down',
  }
  return hqAcceptanceViewById(ids[floor], islandZ)
}

export function hqAcceptanceViewById(
  id: HqAcceptanceViewId,
  islandZ = INTERIOR_ISLAND_Z,
): HqAcceptanceViewPose {
  const poses: Record<HqAcceptanceViewId, Omit<HqAcceptanceViewPose, 'id'>> = {
    'l0-core-up': {
      floor: 0,
      supportY: FLOOR_Y[0],
      x: 1,
      y: FLOOR_Y[0] + 0.1,
      z: islandZ + 5.4,
      yawDeg: 29,
      pitchDeg: 72,
      purpose: 'L0 upward Core, halo and oculus reveal',
    },
    'l0-bay': {
      floor: 0,
      supportY: FLOOR_Y[0],
      x: 8,
      y: FLOOR_Y[0] + 0.1,
      z: islandZ - 6.5,
      yawDeg: 138,
      pitchDeg: -5,
      purpose: 'L0 bay lounge, grounded boardwalk and water seam',
    },
    'l1-bay': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: 10,
      y: FLOOR_Y[1] + 0.1,
      z: islandZ + 8,
      yawDeg: -90,
      pitchDeg: -4,
      purpose: 'L1 populated production pods and cross-void Core',
    },
    'l1-west-rooms': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: -10,
      y: FLOOR_Y[1] + 0.1,
      // Stand on the north approach instead of behind the central grid
      // column, so the focus room door and the paired huddle room read as one
      // west-bay neighborhood.
      z: islandZ - 3.2,
      yawDeg: -90,
      pitchDeg: -3,
      purpose: 'L1 glass focus studio and team huddle room',
    },
    'l1-focus-studio': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: -18.55,
      y: FLOOR_Y[1] + 0.1,
      z: islandZ - 3.15,
      yawDeg: -90,
      pitchDeg: -5,
      purpose: 'L1 Synty focus studio interior, desks and glass entry',
    },
    'l1-team-huddle': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: -18.55,
      y: FLOOR_Y[1] + 0.1,
      z: islandZ + 3.15,
      yawDeg: -90,
      pitchDeg: -5,
      purpose: 'L1 Synty huddle interior, scaled seating and glass entry',
    },
    'l1-synty-characters': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: -9,
      y: FLOOR_Y[1] + 0.1,
      z: islandZ - 14,
      yawDeg: -157,
      pitchDeg: -6,
      purpose: 'TASK-1182/1184 composed L1 production floor, grounded Synty cohort and varied idles',
    },
    'l2-synty-characters': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: -11.5,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ - 9.5,
      yawDeg: -146,
      pitchDeg: -6,
      purpose: 'TASK-1192 L2 production pods, grounded 32-agent roster and floor residency',
    },
    'l0-lounge-detail': {
      floor: 0,
      supportY: FLOOR_Y[0],
      x: 18.3,
      y: FLOOR_Y[0] + 0.1,
      z: islandZ - 4.8,
      yawDeg: 146,
      pitchDeg: -5,
      purpose: 'L0 Synty bay lounge conversation group and clear west approach',
    },
    'l2-boardroom-detail': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: 3.2,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ - 7,
      yawDeg: 72,
      pitchDeg: -5,
      purpose: 'L2 Synty conference table, facing seats and media wall',
    },
    'l2-kitchen-detail': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: 17.6,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ + 7.6,
      yawDeg: 134,
      pitchDeg: -5,
      purpose: 'L2 Synty kitchen counter run and four-seat coffee table',
    },
    'l2-lounge-detail': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: 18.3,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ - 4.4,
      yawDeg: 144,
      pitchDeg: -5,
      purpose: 'L2 Synty bay lounge conversation group',
    },
    'l3-penthouse-detail': {
      floor: 3,
      supportY: FLOOR_Y[3],
      x: 18.2,
      y: FLOOR_Y[3] + 0.1,
      z: islandZ - 5.2,
      yawDeg: 148,
      pitchDeg: -4,
      purpose: 'L3 Synty penthouse lounge and secondary sofa setting',
    },
    'l3-executive-detail': {
      floor: 3,
      supportY: FLOOR_Y[3],
      x: -13.2,
      y: FLOOR_Y[3] + 0.1,
      z: islandZ - 8.5,
      yawDeg: -35,
      pitchDeg: -5,
      purpose: 'L3 Synty executive desk, guest sofa and strategy wall',
    },
    'l2-boardroom-bay': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: 3,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ - 8,
      yawDeg: 90,
      pitchDeg: -4,
      purpose: 'L2 furnished boardroom, collaboration program and bay facade',
    },
    'l3-core-down': {
      floor: 3,
      supportY: 14.1,
      x: 2.8,
      y: 14.2,
      z: islandZ + 6.42,
      yawDeg: 0,
      pitchDeg: -67,
      purpose: 'L3 Core crown and full-shaft downward reveal',
    },
    'l3-bay': {
      floor: 3,
      supportY: FLOOR_Y[3],
      x: 12,
      y: FLOOR_Y[3] + 0.1,
      z: islandZ + 9,
      yawDeg: 16,
      pitchDeg: -2,
      purpose: 'L3 penthouse lounge and highest-altitude bay view',
    },
    'l0-facade-hero': {
      floor: 0,
      supportY: FLOOR_Y[0],
      x: 16,
      y: FLOOR_Y[0] + 0.1,
      z: islandZ - 7.5,
      yawDeg: 90,
      pitchDeg: -7,
      purpose: 'L0 recurring bay hero with boardwalk grounded against water',
    },
    'l1-facade-hero': {
      floor: 1,
      supportY: FLOOR_Y[1],
      x: 16,
      y: FLOOR_Y[1] + 0.1,
      z: islandZ - 7.5,
      yawDeg: 90,
      pitchDeg: -7,
      purpose: 'L1 recurring bay hero above the near-field shore',
    },
    'l2-facade-hero': {
      floor: 2,
      supportY: FLOOR_Y[2],
      x: 16,
      y: FLOOR_Y[2] + 0.1,
      z: islandZ - 7.5,
      yawDeg: 90,
      pitchDeg: -7,
      purpose: 'L2 recurring bay hero with descending cloud deck',
    },
    'l3-facade-hero': {
      floor: 3,
      supportY: FLOOR_Y[3],
      x: 16,
      y: FLOOR_Y[3] + 0.1,
      z: islandZ - 7.5,
      yawDeg: 90,
      pitchDeg: -7,
      purpose: 'L3 recurring bay hero looking down over clouds and water',
    },
  }
  return { id, ...poses[id] }
}

interface WalkState extends Vec3 {
  grounded: boolean
}

interface WalkStats {
  frames: number
  steppedUp: number
  steppedDown: number
  minY: number
  maxY: number
  failures: string[]
}

function walkSegment(
  state: WalkState,
  target: { x: number; z: number },
  colliders: readonly AABB[],
  stats: WalkStats,
  label: string,
): void {
  let previousDistance = Infinity
  let stalledFrames = 0
  for (let frame = 0; frame < MAX_SEGMENT_FRAMES; frame++) {
    const dx = target.x - state.x
    const dz = target.z - state.z
    const distance = Math.hypot(dx, dz)
    if (distance <= TARGET_EPSILON) return
    const step = Math.min(WALK_STEP, distance)
    const result = moveAndResolveCollisions(
      state,
      {
        x: (dx / distance) * step,
        y: -0.05,
        z: (dz / distance) * step,
      },
      PLAYER_RADIUS,
      PLAYER_HEIGHT,
      colliders,
      { wasGrounded: state.grounded, maxStepUp: MAX_STEP, maxStepDown: MAX_STEP },
    )
    state.x = result.x
    state.y = result.y
    state.z = result.z
    state.grounded = result.grounded
    stats.frames += 1
    if (result.steppedUp) stats.steppedUp += 1
    if (result.steppedDown) stats.steppedDown += 1
    stats.minY = Math.min(stats.minY, state.y)
    stats.maxY = Math.max(stats.maxY, state.y)

    const nextDistance = Math.hypot(target.x - state.x, target.z - state.z)
    stalledFrames = nextDistance >= previousDistance - 1e-5
      ? stalledFrames + 1
      : 0
    previousDistance = nextDistance
    if (state.y < -0.5) {
      stats.failures.push(`${label}: fell below the L0 walk surface at y=${state.y.toFixed(3)}`)
      return
    }
    if (stalledFrames >= 20) {
      stats.failures.push(
        `${label}: collision-blocked ${nextDistance.toFixed(3)}m from the waypoint`,
      )
      return
    }
  }
  stats.failures.push(`${label}: exceeded ${MAX_SEGMENT_FRAMES} movement frames`)
}

function floorCheckpoint(
  leg: 'up' | 'down',
  floor: HqAcceptanceFloor,
  state: WalkState,
  checkpoints: HqTraversalCheckpoint[],
  failures: string[],
): void {
  checkpoints.push({ leg, floor, ...state })
  const expectedY = FLOOR_Y[floor]
  if (!state.grounded || Math.abs(state.y - expectedY) > 0.03) {
    failures.push(
      `${leg} L${floor}: expected grounded y=${expectedY.toFixed(2)}, got ` +
      `grounded=${String(state.grounded)} y=${state.y.toFixed(3)}`,
    )
  }
}

/**
 * Walk the exact switchback geometry up and back down using the production
 * capsule resolver. This is synchronous and deterministic, so the browser
 * artifact proves the currently registered real scene colliders—not a mock
 * camera path—remain traversable in both directions.
 */
export function runHqTraversalAcceptance(
  colliders: readonly AABB[],
  islandZ = INTERIOR_ISLAND_Z,
  capturedAt = new Date().toISOString(),
): HqTraversalArtifact {
  const state: WalkState = {
    x: -7.3,
    y: FLOOR_Y[0],
    z: islandZ - 6.15,
    grounded: true,
  }
  const stats: WalkStats = {
    frames: 0,
    steppedUp: 0,
    steppedDown: 0,
    minY: state.y,
    maxY: state.y,
    failures: [],
  }
  const checkpoints: HqTraversalCheckpoint[] = []
  floorCheckpoint('up', 0, state, checkpoints, stats.failures)

  for (let storey = 0; storey < 3; storey++) {
    walkSegment(
      state,
      { x: -7.3, z: islandZ - 1.5 },
      colliders,
      stats,
      `L${storey} flight A up`,
    )
    walkSegment(
      state,
      { x: -4.3, z: islandZ - 1.5 },
      colliders,
      stats,
      `L${storey} mid-landing across`,
    )
    walkSegment(
      state,
      { x: -4.3, z: islandZ - 5.875 },
      colliders,
      stats,
      `L${storey} flight B up`,
    )
    floorCheckpoint(
      'up',
      (storey + 1) as HqAcceptanceFloor,
      state,
      checkpoints,
      stats.failures,
    )
    if (storey < 2) {
      walkSegment(
        state,
        { x: -7.3, z: islandZ - 5.875 },
        colliders,
        stats,
        `L${storey + 1} arrival across`,
      )
    }
  }

  for (let floor = 3; floor > 0; floor--) {
    walkSegment(
      state,
      { x: -4.3, z: islandZ - 1.5 },
      colliders,
      stats,
      `L${floor} flight B down`,
    )
    walkSegment(
      state,
      { x: -7.3, z: islandZ - 1.5 },
      colliders,
      stats,
      `L${floor} mid-landing across`,
    )
    walkSegment(
      state,
      { x: -7.3, z: islandZ - 6.15 },
      colliders,
      stats,
      `L${floor} flight A down`,
    )
    floorCheckpoint(
      'down',
      (floor - 1) as HqAcceptanceFloor,
      state,
      checkpoints,
      stats.failures,
    )
    if (floor > 1) {
      walkSegment(
        state,
        { x: -4.3, z: islandZ - 5.875 },
        colliders,
        stats,
        `L${floor - 1} arrival across`,
      )
    }
  }

  if (stats.steppedUp < 60) {
    stats.failures.push(`expected at least 60 upward stair transitions, got ${stats.steppedUp}`)
  }
  if (stats.steppedDown < 60) {
    stats.failures.push(`expected at least 60 downward stair transitions, got ${stats.steppedDown}`)
  }
  return {
    schema: 'arena.hq-traversal.v1',
    status: 'measured',
    capturedAt,
    colliderCount: colliders.length,
    frames: stats.frames,
    steppedUp: stats.steppedUp,
    steppedDown: stats.steppedDown,
    minY: stats.minY,
    maxY: stats.maxY,
    checkpoints,
    failures: stats.failures,
    passed: stats.failures.length === 0,
  }
}

const REQUIRED_DOOR_ZONE_IDS = new Set([
  'l0-server-room',
  'l1-focus-studio',
  'l1-team-huddle',
  'l2-boardroom',
  'l2-kitchen',
])

function checkpointInsideZone(
  zone: HqProgramAcceptanceZone,
): { x: number; z: number } {
  const [accessX, accessZ] = zone.access
  const width = zone.bounds.maxX - zone.bounds.minX
  const depth = zone.bounds.maxZ - zone.bounds.minZ
  const insetX = Math.min(0.7, width / 2)
  const insetZ = Math.min(0.7, depth / 2)
  const distances = [
    { edge: 'west', distance: Math.abs(accessX - zone.bounds.minX) },
    { edge: 'east', distance: Math.abs(accessX - zone.bounds.maxX) },
    { edge: 'north', distance: Math.abs(accessZ - zone.bounds.minZ) },
    { edge: 'south', distance: Math.abs(accessZ - zone.bounds.maxZ) },
  ] as const
  const edge = [...distances].sort((a, b) => a.distance - b.distance)[0].edge
  return {
    x: edge === 'west'
      ? zone.bounds.minX + insetX
      : edge === 'east'
        ? zone.bounds.maxX - insetX
        : Math.min(zone.bounds.maxX - insetX, Math.max(zone.bounds.minX + insetX, accessX)),
    z: edge === 'north'
      ? zone.bounds.minZ + insetZ
      : edge === 'south'
        ? zone.bounds.maxZ - insetZ
        : Math.min(zone.bounds.maxZ - insetZ, Math.max(zone.bounds.minZ + insetZ, accessZ)),
  }
}

/**
 * Visit every declared program door/checkpoint with the production capsule.
 * Each visit starts at the zone's circulation-ring attachment so a failure is
 * attributable to that zone rather than cascading from an earlier doorway.
 */
export function runHqProgramTraversalAcceptance(
  colliders: readonly AABB[],
  circulation: readonly HqProgramAcceptanceFloor[],
  zones: readonly HqProgramAcceptanceZone[],
  islandZ = INTERIOR_ISLAND_Z,
  capturedAt = new Date().toISOString(),
): HqProgramTraversalArtifact {
  const checkpoints: HqProgramTraversalCheckpoint[] = []
  const failures: string[] = []
  let frames = 0

  for (const zone of zones) {
    const floor = circulation.find((entry) => entry.floor === zone.floor)
    const route = floor?.nodes.find((node) => node.id === zone.routeNode)
    if (!route) {
      failures.push(`${zone.id}: missing circulation route node ${zone.routeNode}`)
      continue
    }
    const expectedY = FLOOR_Y[zone.floor]
    const state: WalkState = {
      x: route.x,
      y: expectedY,
      z: islandZ + route.z,
      grounded: true,
    }
    const stats: WalkStats = {
      frames: 0,
      steppedUp: 0,
      steppedDown: 0,
      minY: expectedY,
      maxY: expectedY,
      failures: [],
    }
    walkSegment(
      state,
      { x: zone.access[0], z: islandZ + zone.access[1] },
      colliders,
      stats,
      `${zone.id} doorway`,
    )
    const doorwayReached = stats.failures.length === 0
      && Math.hypot(state.x - zone.access[0], state.z - (islandZ + zone.access[1]))
        <= TARGET_EPSILON
    const target = checkpointInsideZone(zone)
    if (doorwayReached) {
      walkSegment(
        state,
        { x: target.x, z: islandZ + target.z },
        colliders,
        stats,
        `${zone.id} checkpoint`,
      )
    }
    frames += stats.frames
    failures.push(...stats.failures)
    const localZ = state.z - islandZ
    const inBounds =
      state.x >= zone.bounds.minX - 0.04
      && state.x <= zone.bounds.maxX + 0.04
      && localZ >= zone.bounds.minZ - 0.04
      && localZ <= zone.bounds.maxZ + 0.04
    const grounded = state.grounded && Math.abs(state.y - expectedY) <= 0.03
    const noFall = stats.minY >= expectedY - 0.05
    if (!doorwayReached) failures.push(`${zone.id}: declared doorway was not reached`)
    if (!inBounds) failures.push(`${zone.id}: checkpoint is outside its reserved footprint`)
    if (!grounded) failures.push(`${zone.id}: checkpoint is not grounded at L${zone.floor}`)
    if (!noFall) failures.push(`${zone.id}: fell below the L${zone.floor} walk surface`)
    checkpoints.push({
      zoneId: zone.id,
      floor: zone.floor,
      x: state.x,
      y: state.y,
      z: localZ,
      grounded,
      inBounds,
      noFall,
      doorwayReached,
    })
  }

  const zoneCountPass = zones.length === 24 && checkpoints.length === 24
  const groundedPass = checkpoints.every((checkpoint) => checkpoint.grounded)
  const inBoundsPass = checkpoints.every((checkpoint) => checkpoint.inBounds)
  const noFallPass = checkpoints.every((checkpoint) => checkpoint.noFall)
  const doorwayPass = [...REQUIRED_DOOR_ZONE_IDS].every((zoneId) =>
    checkpoints.some((checkpoint) => checkpoint.zoneId === zoneId && checkpoint.doorwayReached))
  if (!zoneCountPass) failures.push(`expected 24 program checkpoints, captured ${checkpoints.length}`)
  const passed = zoneCountPass
    && groundedPass
    && inBoundsPass
    && noFallPass
    && doorwayPass
    && failures.length === 0
  return {
    schema: 'arena.hq-program-traversal.v1',
    status: 'measured',
    capturedAt,
    colliderCount: colliders.length,
    frames,
    checkpoints,
    failures,
    gate: {
      expectedZoneCount: 24,
      zoneCountPass,
      groundedPass,
      inBoundsPass,
      noFallPass,
      doorwayPass,
      passed,
    },
    passed,
  }
}
